export type Verdict = 'APPROVE' | 'BLOCK';

export interface ReviewIssue {
  file: string;
  line: number;
  description: string;
}

export interface ReviewFollowupRecommendation {
  file: string;
  line: number;
  description: string;
}

export interface ReviewResult {
  verdict: Verdict;
  blocking_issues: ReviewIssue[];
  followup_recommendations: ReviewFollowupRecommendation[];
  summary: string;
  rebuttal_response: string;
  close_recommended: boolean;
}

export const MAX_REVIEW_ITEMS = 10;
export const MAX_REVIEW_FILE_CHARS = 300;
export const MAX_REVIEW_DESCRIPTION_CHARS = 1000;
export const MAX_REVIEW_SUMMARY_CHARS = 4000;
export const MAX_REBUTTAL_RESPONSE_CHARS = 2500;

export const REVIEW_SCHEMA = {
  type: 'object',
  properties: {
    verdict: {
      type: 'string',
      enum: ['APPROVE', 'BLOCK'],
    },
    blocking_issues: {
      type: 'array',
      maxItems: MAX_REVIEW_ITEMS,
      description: 'Findings that must be resolved in this PR before merge and must not be deferred to a follow-up.',
      items: {
        type: 'object',
        properties: {
          file: { type: 'string', minLength: 1, maxLength: MAX_REVIEW_FILE_CHARS },
          line: { type: 'integer', minimum: 1 },
          description: { type: 'string', minLength: 1, maxLength: MAX_REVIEW_DESCRIPTION_CHARS },
        },
        required: ['file', 'line', 'description'],
        additionalProperties: false,
      },
    },
    followup_recommendations: {
      type: 'array',
      maxItems: MAX_REVIEW_ITEMS,
      description: 'Non-blocking improvements that are safe to defer until after this PR merges. Never include a required fix here.',
      items: {
        type: 'object',
        properties: {
          file: { type: 'string', minLength: 1, maxLength: MAX_REVIEW_FILE_CHARS },
          line: { type: 'integer', minimum: 1 },
          description: { type: 'string', minLength: 1, maxLength: MAX_REVIEW_DESCRIPTION_CHARS },
        },
        required: ['file', 'line', 'description'],
        additionalProperties: false,
      },
    },
    summary: { type: 'string', maxLength: MAX_REVIEW_SUMMARY_CHARS },
    rebuttal_response: { type: 'string', maxLength: MAX_REBUTTAL_RESPONSE_CHARS },
    close_recommended: { type: 'boolean' },
  },
  required: [
    'verdict',
    'blocking_issues',
    'followup_recommendations',
    'summary',
    'rebuttal_response',
    'close_recommended',
  ],
  additionalProperties: false,
} as const;
